/**
 * Whether a county's parcel score can rank anything, and how to say so if not.
 *
 * `WEIGHTS` totals 230 points across fourteen signals, and a county can only
 * score on the signals it can actually evidence — that is what
 * `SIGNALS_BY_COUNTY` is for, and why `/api/counties` serves it. The
 * consequence nobody had stated until it was measured: **in a county that
 * declares one signal, every scored parcel carries the same score.** Indian
 * River's full pull is 3,765 scored units and one distinct score, 10. Six of
 * the live counties are in that position.
 *
 * That is not a bug in the scoring — a signal either fired or it did not, and
 * the row is honest about which. It is a claim the *column heading* makes and
 * the data cannot support: a column a subscriber reads as "how bad is this
 * one" is, in those counties, a column that says "a signal fired". Sorting by
 * it does nothing.
 *
 * So this is the `lib/views.ts` treatment applied to the score: a pure
 * function with tests, deciding a product question that would otherwise be an
 * `if` buried in a component. And it is **derived from the county's signal
 * list rather than written per county**, because the masthead has already gone
 * stale once by hard-coding what a county could not do — it told Broward users
 * there was no permit history for months after there was.
 */

/**
 * Signals that mark every unit of a building rather than one parcel.
 *
 * They separate rows only where a building is marked, which is sparse — 529 of
 * 27,972 condominiums reported a SIRS special assessment — so counting one
 * would tell a county with a single parcel signal that its scores rank when,
 * for almost every row, they do not. The note says both halves instead.
 */
export const BUILDING_WIDE_SIGNALS = ["sirs_assessment"];

/** What a county's signal set means for its parcel score column. */
export interface ScoreScope {
  /** False when every scored parcel in the county carries the same score. */
  ranks: boolean;
  /** The sentence to show beside the score, or null when it does rank. */
  note: string | null;
}

/**
 * Read a county's declared signals.
 *
 * `undefined` means "every county" or "not loaded yet", and both must return
 * `ranks: true` with no note — a caveat flashed before the registry loads
 * would be a claim about a county nobody has chosen yet, and one shown across
 * the whole dataset would be false of Miami-Dade.
 */
export function scoreScope(signals?: string[]): ScoreScope {
  if (!signals) return { ranks: true, note: null };
  const parcel = signals.filter((s) => !BUILDING_WIDE_SIGNALS.includes(s));
  if (parcel.length > 1) return { ranks: true, note: null };

  // Zero is not a state any live county is in — every one declares at least
  // `absentee` — but a county whose feed we lose would land here, and the
  // sentence is true of it either way.
  if (signals.includes("sirs_assessment")) {
    return {
      ranks: false,
      note:
        "This county publishes one signal we can evidence on the parcel " +
        "itself, so scored parcels here carry the same score unless their " +
        "building's association reported a SIRS special assessment, which " +
        "adds to every unit in it. Use it to tell scored from unscored, not " +
        "to rank one property against another.",
    };
  }
  return {
    ranks: false,
    note:
      "This county publishes one signal we can evidence, so every scored " +
      "parcel here carries the same score. Use it to tell scored from " +
      "unscored, not to rank one property against another.",
  };
}
